import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getUserData } from '../util';
import { invokeTypedService } from '../services';
import type { SearchCriteriaElements, ResortDimensionsResponse, DepositsOwnershipsResponse } from './types';
import type { RootState } from '../../../store/store.types';
import { logErrorToSplunk } from '../../../service';

interface FilterState {
  resortDimensions: ResortDimensionsResponse | null;
  depositsOwnerships: DepositsOwnershipsResponse | null;
  isLoading: boolean;
  isLoadingDeposits: boolean;
  error: string | null;
  depositsError: string | null;
  isRegionSelected: boolean | null;
  isFilterResetRequested: boolean; // Add flag for filter reset
}

const initialState: FilterState = {
  resortDimensions: null,
  depositsOwnerships: null,
  isLoading: false,
  isLoadingDeposits: false,
  error: null,
  depositsError: null,
  isRegionSelected: false,
  isFilterResetRequested: false, // Initialize to false
};

export const fetchResortDimensions = createAsyncThunk<
  ResortDimensionsResponse,
  SearchCriteriaElements, // Accepts SearchCriteriaElements as payload
  { state: RootState; rejectValue: string }
>(
  'filter/fetchResortDimensions',
  async (searchCriteriaElements, { rejectWithValue }) => {
    // Build query params dynamically like typeaheadApiUrl
    const queryParams = new URLSearchParams({
      customerIdQ: getUserData().memberId,
    });
    // const payloadWithVersion = {
    //   ...searchCriteriaElements,
    //   version: 'v3',
    // };
    const resource = `/resort-operations/v1/resorts/weeks/dimensions?${queryParams.toString()}`;
    try {
      const response = await invokeTypedService<ResortDimensionsResponse>({
        resource,
        method: 'POST',
        data: searchCriteriaElements,
      });

      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue('No data returned from API');
      }
    } catch (error) {
      // Log error if logging service is available
      try {
        logErrorToSplunk(error, resource, 'POST', 'Dimensions - fetchResortDimensions');
      } catch (logError) {
        console.error('Error logging failed:', logError);
      }
      return rejectWithValue((error as Error).message);
    }
  }
);

// export const fetchResortDimensionsBeforeSelection = createAsyncThunk<
//   ResortDimensionsResponse,
//   SearchCriteriaElements, // Accepts SearchCriteriaElements as payload
//   { state: RootState; rejectValue: string }
// >(
//   'filter/fetchResortDimensionsBeforeSelection',
//   async (searchCriteriaElements, { rejectWithValue }) => {
//     try {
//       // Build query params dynamically like typeaheadApiUrl
//       const queryParams = new URLSearchParams({
//         customerIdQ: getUserData().memberId,
//       });
//       const resource = `/resort-operations/v1/resorts/weeks/dimensions?${queryParams.toString()}`;

//       const response = await invokeTypedService<ResortDimensionsResponse>({
//         resource,
//         method: 'POST',
//         data: searchCriteriaElements,
//       });

//       if (response.data) {
//         return response.data;
//       } else {
//         return rejectWithValue('No data returned from API');
//       }
//     } catch (error) {
//       return rejectWithValue((error as Error).message);
//     }
//   }
// );

export const fetchDepositsOwnerships = createAsyncThunk<
  DepositsOwnershipsResponse,
  void, // No payload required
  { state: RootState; rejectValue: string }
>(
  'filter/fetchDepositsOwnerships',
  async (_, { rejectWithValue }) => {
    const userData = getUserData();

    // Build query params
    const queryParams = new URLSearchParams({
      customerIdQ: userData.memberId,
    });

    const resource = `/rcio/${userData.memberId}/ownerships/deposits?${queryParams.toString()}`;
    try {
      const response = await invokeTypedService<DepositsOwnershipsResponse>({
        resource,
        method: 'GET',
      });

      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue('No data returned from API');
      }
    } catch (error) {
      try {
        logErrorToSplunk(error, resource, 'GET', 'Deposits - fetchDepositsOwnerships');
      } catch (logError) {
        console.error('Error logging failed:', logError);
      }
      return rejectWithValue((error as Error).message);
    }
  }
);

const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    clearResortDimensions(state) {
      state.resortDimensions = null;
      //state.error = null;
    },
    applySelection(state) {
      state.isRegionSelected = true;
      //state.error = null;
    },
    removeSelection(state) {
      state.isRegionSelected = false;
      // state.resortDimensions = null;
      //state.error = null;
    },
    clearDepositsOwnerships(state) {
      state.depositsOwnerships = null;
      state.depositsError = null;
    },
    requestFilterReset(state) {
      state.isFilterResetRequested = true;
    },
    acknowledgeFilterReset(state) {
      state.isFilterResetRequested = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Resort Dimensions cases
      .addCase(fetchResortDimensions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchResortDimensions.fulfilled, (state, action: PayloadAction<ResortDimensionsResponse>) => {
        state.isLoading = false;
        state.error = null;
        state.resortDimensions = action.payload;
      })
      .addCase(fetchResortDimensions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
    
      // Deposits Ownerships cases
      .addCase(fetchDepositsOwnerships.pending, (state) => {
        state.isLoadingDeposits = true;
        state.depositsError = null;
      })
      .addCase(fetchDepositsOwnerships.fulfilled, (state, action: PayloadAction<DepositsOwnershipsResponse>) => {
        state.isLoadingDeposits = false;
        state.depositsOwnerships = action.payload;
      })
      .addCase(fetchDepositsOwnerships.rejected, (state, action) => {
        state.isLoadingDeposits = false;
        state.depositsError = action.payload as string;
      });
  },
});

export const { clearResortDimensions, clearDepositsOwnerships, applySelection, removeSelection, requestFilterReset, acknowledgeFilterReset } = filterSlice.actions;

// Resort Dimensions selectors
export const selectResortDimensions = (state: { filter: FilterState }) => state.filter.resortDimensions;
export const selectFilterLoading = (state: { filter: FilterState }) => state.filter.isLoading;
export const selectFilterError = (state: { filter: FilterState }) => state.filter.error;

// Deposits Ownerships selectors
export const selectDepositsOwnerships = (state: { filter: FilterState }) => state.filter.depositsOwnerships;
export const selectDepositsLoading = (state: { filter: FilterState }) => state.filter.isLoadingDeposits;
export const selectDepositsError = (state: { filter: FilterState }) => state.filter.depositsError;

// Filter reset selector
export const selectIsFilterResetRequested = (state: { filter: FilterState }) => state.filter.isFilterResetRequested;

// Add this selector at the end with other selectors

export default filterSlice.reducer;

